import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { inviteToEscritorio } from '@/api/client';
import { Button } from '@/components/ui/button';
import { CopyButton } from '@/components/ui/copy-button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { useNotification } from '@/context/NotificationProvider';
import { notifyMutationError } from '@/lib/errors';
import { queryKeys } from '@/lib/pwa/queryConfig';

import { PAPEIS_DO_CONVITE, ROLE_LABELS } from './papeis';

const AJUDA_DO_PAPEL: Record<string, string> = {
  SOCIO: 'Cuida dos membros, convites e equipes, junto com o Proprietário.',
  ASSOCIADO: 'Trabalha no que foi compartilhado e protocola com o próprio certificado.',
  ESTAGIARIO: 'Vê e prepara (minuta, documento, prazo), sem protocolar.',
};

interface ConvidarDialogProps {
  escritorioId: string;
  escritorioNome: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Convidar por e-mail; depois de enviado, o mesmo diálogo mostra o link do convite. */
export function ConvidarDialog(props: ConvidarDialogProps) {
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent className="sm:max-w-md">
        {/* Montado a cada abertura: começa limpo, sem efeito que zere o estado. */}
        {props.open && <Corpo {...props} />}
      </DialogContent>
    </Dialog>
  );
}

function Corpo({ escritorioId, escritorioNome, onOpenChange }: ConvidarDialogProps) {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const [email, setEmail] = useState('');
  const [papel, setPapel] = useState<string>('ASSOCIADO');
  const [link, setLink] = useState<string | null>(null);

  const convidar = useMutation({
    mutationFn: () => inviteToEscritorio(escritorioId, { email: email.trim(), role: papel }),
    onSuccess: (convite) => {
      showSuccess('Convite enviado');
      queryClient.invalidateQueries({
        queryKey: queryKeys.escritorios.convites(escritorioId),
      });
      if (convite.url_convite) setLink(convite.url_convite);
      else onOpenChange(false);
    },
    onError: (err) => notifyMutationError(err, showError),
  });

  return (
    <>
      <DialogHeader>
        <DialogTitle>{link ? 'Convite enviado' : 'Convidar para o escritório'}</DialogTitle>
        <DialogDescription>
          {link
            ? 'O convite foi por e-mail. Se precisar, mande também este link:'
            : `O convite chega por e-mail e vale para "${escritorioNome}".`}
        </DialogDescription>
      </DialogHeader>
      {link ? (
        <>
          <div className="flex items-center gap-2">
            <Input value={link} readOnly className="text-xs" aria-label="Link do convite" />
            <CopyButton text={link} label="Copiar link" />
          </div>
          <DialogFooter>
            <Button onClick={() => onOpenChange(false)}>Fechar</Button>
          </DialogFooter>
        </>
      ) : (
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (email.trim()) convidar.mutate();
          }}
        >
          <div className="space-y-2">
            <Label htmlFor="convite-email">E-mail</Label>
            <Input
              id="convite-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="advogado@exemplo.com"
              autoFocus
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="convite-papel">Papel</Label>
            <Select value={papel} onValueChange={setPapel}>
              <SelectTrigger id="convite-papel">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAPEIS_DO_CONVITE.map((p) => (
                  <SelectItem key={p} value={p}>
                    {ROLE_LABELS[p]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">{AJUDA_DO_PAPEL[papel]}</p>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={convidar.isPending || !email.trim()}>
              {convidar.isPending && <Spinner size={16} className="mr-2 text-inherit" />}
              Enviar convite
            </Button>
          </DialogFooter>
        </form>
      )}
    </>
  );
}
